import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { ButtonLink } from 'js/design_system/link/ButtonLink';

type Props = {
  query: string;
  url: string;
};

const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  margin-bottom: var(--spacing-sm-32, 32px);
  padding: var(--spacing-sm-24, 24px);
  border: 1px solid var(--background-beige-dark, #e2e0d9);
  border-radius: var(--border-radius-radius-md, 8px);
  background: var(--background-beige-light, #f9f8f6);

  @include breakpoint(medium, large) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const Copy = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
`;

const Title = styled(Heading).attrs({ as: 'h2', size: 'sm' })`
  margin: 0;
`;

const Description = styled(Body).attrs({ size: 'sm' })`
  max-width: 720px;
  margin: 0;
  color: var(--text-beige-secondary, #585448);
`;

export const PaperFinderUpsell: React.FC<Props> = ({ query, url }) => (
  <Container>
    <Copy>
      <Title>Closed access papers do not have to stay closed</Title>
      <Description>
        Some recently published papers about &apos;{query}&apos; are closed
        access. Pick the ones you&apos;re interested in and we will try to get
        you the full text.
      </Description>
    </Copy>
    <ButtonLink href={url}>Find papers</ButtonLink>
  </Container>
);

const preflightUrlFor = (url: string): string => {
  const preflightUrl = new URL(url, window.location.origin);
  preflightUrl.pathname = `${preflightUrl.pathname}.json`;
  preflightUrl.searchParams.set('preflight', 'true');
  return `${preflightUrl.pathname}${preflightUrl.search}`;
};

export const PaperFinderUpsellPreflight: React.FC<Props> = ({ query, url }) => {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    setAvailable(false);
    let cancelled = false;

    fetch(preflightUrlFor(url))
      .then(async (response) => {
        if (!response.ok) return {};
        return (await response.json()) as { results_available?: boolean };
      })
      .then(({ results_available: resultsAvailable }) => {
        if (!cancelled) setAvailable(resultsAvailable === true);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!available) return null;

  return <PaperFinderUpsell query={query} url={url} />;
};
